import 'twin.macro';
import React, { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { DeleteWeekWarningModal } from './DeleteWeekWarningModal';
import { Menu } from '@teetsh/app/src/components/Menu';
import {
  AdjustmentsVerticalIcon,
  ChartPieIcon,
  ChatBubbleBottomCenterIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { getClassroomConfigurationLink } from '@teetsh/app/src/utils/urls';
import { Filters } from './Filters';
import { CJViewSettingsModal } from './CJViewSettingsModal';
import { AIContextualActionsModal } from './AIContextualActionsModal';
import { Volume } from './Volume';
import { CahierJournalView } from '../types';
import { useTranslation } from 'react-i18next';
import { Share } from './Share';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { PreviewDownloadButton } from './PreviewDownloadButton';
import { format } from 'utils/time/format';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { UndoRedo } from 'containers/CahierJournal/Toolbar/UndoRedo';
import Button from 'components/Buttons/Button';
import Expand from 'components/Icons/icons/Expand';
import Collapse from 'components/Icons/icons/Collapse';
import {
  useCollapsedDays,
  useToggleMultipleCollapsedDay,
} from 'containers/CahierJournal/atoms';
import getDaysBetween from 'utils/time/getDaysBetween';
import { SingleDayMenuItems } from 'containers/CahierJournal/DayColumn/Header/SingleDayMenuItems';

interface Props {
  date: Date;
  from: Date;
  to: Date;
  view: CahierJournalView;
  previewLink: string;
}

export function Actions(props: Props) {
  const { date, from, to, view, previewLink } = props;
  const { guestMode } = useUser();
  const [isVolumeModalOpen, setIsVolumeModalOpen] = useState<boolean>(false);
  const toggleCollapsedCJ = useToggleMultipleCollapsedDay();
  const collapsedDays = useCollapsedDays();
  const [isRemoveEventsModalOpen, setIsRemoveEventsModalOpen] =
    useState<boolean>(false);
  const [isCJViewSettingsOpen, setIsCJViewSettingsOpen] =
    useState<boolean>(false);
  const [isAIConfigModalOpen, setIsAIConfigModalOpen] =
    useState<boolean>(false);
  const { schoolyear, school } = useSchoolSchoolyear();

  const { t } = useTranslation(['common', 'cahierJournal']);

  return (
    <>
      <UndoRedo minimal />
      <DeleteWeekWarningModal
        isOpen={isRemoveEventsModalOpen}
        from={from}
        to={to}
        onClose={() => setIsRemoveEventsModalOpen(false)}
      />
      <CJViewSettingsModal
        isOpen={isCJViewSettingsOpen}
        onClose={() => setIsCJViewSettingsOpen(false)}
      />
      <AIContextualActionsModal
        isOpen={isAIConfigModalOpen}
        onClose={() => setIsAIConfigModalOpen(false)}
      />
      <Tooltip2 label={t('cahierJournal:dayHeader.condensedView')}>
        <Button
          onClick={() =>
            toggleCollapsedCJ(
              getDaysBetween(from, to).map(d => format(d, 'fr-FR')),
            )
          }
          intent={(collapsedDays || []).length > 0 ? 'primary' : 'none'}
          minimal
          icon={(collapsedDays || []).length > 0 ? Expand : (Collapse as any)}
        />
      </Tooltip2>
      <PreviewDownloadButton previewLink={previewLink} />
      {!guestMode?.enabled && <Share />}
      <Filters />
      <Volume
        onClose={() => setIsVolumeModalOpen(false)}
        isOpen={isVolumeModalOpen}
        from={from}
        to={to}
      />
      <Menu portal>
        <Tooltip2 label={t('cahierJournal:toolbar.moreOptions')}>
          <Menu.Button
            icon={EllipsisHorizontalIcon}
            aria-label="Préférences cahier journal semaine"
            minimal
          />
        </Tooltip2>
        <Menu.Items size="auto">
          <div tw="py-1">
            <Menu.Item
              icon={ChartPieIcon}
              onClick={() => setIsVolumeModalOpen(true)}
              text={t('cahierJournal:toolbar.volume.label')}
            />
            <Menu.Item
              icon={AdjustmentsVerticalIcon}
              onClick={() => setIsCJViewSettingsOpen(true)}
              text={t('cahierJournal:settings.cjView.title')}
            />
            <Menu.Item
              icon={ChatBubbleBottomCenterIcon}
              onClick={() => setIsAIConfigModalOpen(true)}
              text={t('common:buttons.configureContextualActions')}
            />

            {!guestMode?.enabled && (
              <>
                <Menu.Item
                  to={getClassroomConfigurationLink({
                    schoolyearId: schoolyear.id,
                    schoolId: school.id,
                  })}
                  // Je sais pas pourquoi cet icon est naurellement plus grand
                  iconSize="sm"
                  icon={Cog6ToothIcon}
                  text={t('cahierJournal:settings.classroomSettings')}
                />
              </>
            )}
          </div>
          {!guestMode?.enabled && view !== 'day' && (
            <div tw="py-1">
              <Menu.Item
                icon={TrashIcon}
                intent="danger"
                tw="whitespace-nowrap"
                onClick={() => setIsRemoveEventsModalOpen(true)}
                text={t('cahierJournal:toolbar.deleteWeek.action')}
              />
            </div>
          )}
          {view === 'day' && (
            <SingleDayMenuItems
              day={date}
              view={view}
              previewLink={previewLink}
            />
          )}
        </Menu.Items>
      </Menu>
    </>
  );
}
